import React, { useEffect, useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../lib/errors';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { FileDrop } from '../../components/import/ImportPrimitives';
import { CalendarClock, Download, Upload, Loader2, ShieldCheck, CheckCircle2, AlertTriangle, Wand2, Lock, Search, ListChecks } from 'lucide-react';
import HoursTypeMapCard from './HoursTypeMapCard';
import ExtraPayCard from './ExtraPayCard';

const PAY_GROUPS = ['officer', 'salaried'];

function downloadTemplate(kind, filename) {
  payrollAPI.template(kind)
    .then(({ data }) => {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch(() => toast.error('Could not download template'));
}

function TemplateButton({ kind, filename, testId }) {
  return (
    <Button size="sm" variant="outline" onClick={() => downloadTemplate(kind, filename)} data-testid={testId}>
      <Download className="w-4 h-4 mr-1" /> Download template
    </Button>
  );
}

function ReviewTable({ review, cols }) {
  if (!review) return null;
  return (
    <div className="space-y-2" data-testid="payroll-review-table">
      <div className="flex gap-2 text-sm">
        <Badge variant="secondary">{review.total} rows</Badge>
        <Badge className="bg-emerald-600">{review.valid_count} valid</Badge>
        {review.error_count > 0 && <Badge variant="destructive">{review.error_count} with errors</Badge>}
      </div>
      <div className="overflow-auto max-h-80 border rounded">
        <Table>
          <TableHeader><TableRow>
            {cols.map((c) => <TableHead key={c}>{c}</TableHead>)}
            <TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {review.rows.map((r, i) => (
              <TableRow key={i} className={r.valid ? '' : 'bg-rose-50'} data-testid={`payroll-review-row-${i}`}>
                {cols.map((c) => <TableCell key={c} className="text-xs">{String(r[c] ?? '')}</TableCell>)}
                <TableCell className="text-xs">
                  {r.valid ? <span className="text-emerald-700 inline-flex items-center"><CheckCircle2 className="w-3 h-3 mr-1" />OK</span>
                    : <span className="text-rose-700">{r.errors.join('; ')}</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function GeneratePeriods() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [adjust, setAdjust] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await payrollAPI.generatePeriods(Number(year), Number(month), adjust, dryRun);
      setPreview(data);
      if (!dryRun) toast.success('Pay periods saved');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not generate pay periods'));
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="payroll-generate-periods">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Generate pay periods</CardTitle>
        <CardDescription>Preview the two periods and pay dates for a month (including any prior-business-day shifts), then confirm to save.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="text-xs text-slate-500">Year</label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-28" data-testid="pp-year" /></div>
          <div><label className="text-xs text-slate-500">Month</label><Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(e.target.value)} className="w-24" data-testid="pp-month" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={adjust} onChange={(e) => setAdjust(e.target.checked)} data-testid="pp-adjust" /> Shift weekend/holiday pay dates to the prior business day</label>
          <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="pp-preview-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}</Button>
          <Button onClick={() => run(false)} disabled={busy || !preview} data-testid="pp-confirm-btn">Confirm &amp; save</Button>
        </div>
        {preview && (
          <div className="space-y-2">
            {preview.preview && <Badge variant="outline">Preview only — not saved yet</Badge>}
            <div className="overflow-auto border rounded">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Pay group</TableHead><TableHead>Period</TableHead>
                  <TableHead>Pay date</TableHead><TableHead>Original</TableHead><TableHead>Shift</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(preview.created || []).map((p) => (
                    <TableRow key={p.id} className={p.pay_date_adjusted ? 'bg-amber-50' : ''} data-testid={`pp-row-${p.id}`}>
                      <TableCell className="capitalize text-sm">{p.pay_group}</TableCell>
                      <TableCell className="text-xs">{p.period_start} → {p.period_end}</TableCell>
                      <TableCell className="font-medium text-sm">{p.pay_date}</TableCell>
                      <TableCell className="text-xs text-slate-500">{p.pay_date_raw}</TableCell>
                      <TableCell className="text-xs">{p.pay_date_adjusted ? <Badge variant="outline" className="text-amber-700">{p.adjustment_reason}</Badge> : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CsvUpload({ title, description, kind, templateFile, cols, reviewFn, commitFn, onSaved }) {
  const [file, setFile] = useState(null);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doReview = async () => {
    if (!file) { toast.error('Choose a CSV'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await reviewFn(fd);
      setReview(data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not read the CSV'));
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await commitFn(fd);
      toast.success(`Saved ${data.inserted} row(s)`);
      setReview(null); setFile(null);
      onSaved && onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Nothing saved — fix the flagged rows'));
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid={`payroll-upload-${kind}`}>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> {title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <TemplateButton kind={kind} filename={templateFile} testId={`template-${kind}`} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FileDrop label="CSV or Excel file" hint="Drop a .xlsx or .csv, or click to choose" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" file={file} onFile={(f) => { setFile(f); setReview(null); }} testId={`filedrop-${kind}`} />
        <div className="flex gap-2">
          <Button variant="outline" onClick={doReview} disabled={busy || !file} data-testid={`review-${kind}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Review'}</Button>
          <Button onClick={doCommit} disabled={busy || !review || review.error_count > 0} data-testid={`commit-${kind}`}>Commit</Button>
        </div>
        {review && review.error_count > 0 && (
          <Alert className="border-rose-200 bg-rose-50"><AlertTriangle className="h-4 w-4 text-rose-600" /><AlertDescription className="text-rose-800 text-sm">Fix the flagged rows before committing — nothing is saved until every row is valid.</AlertDescription></Alert>
        )}
        <ReviewTable review={review} cols={cols} />
      </CardContent>
    </Card>
  );
}

function SuggestPayGroups({ onSaved }) {
  const [rows, setRows] = useState(null);
  const [restricted, setRestricted] = useState(null);
  const [eff, setEff] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await payrollAPI.payGroupSuggest();
      // Pre-select ONLY the system suggestion. Employees with no comp type get a
      // blank choice and are excluded from Save until someone picks one — never
      // silently defaulted to 'officer'.
      setRows((data.suggestions || []).map((s) => ({ ...s, chosen: s.suggested_pay_group || '' })));
      setRestricted(data.restricted_note || null);
    } catch (e) { toast.error(extractErrorMessage(e, 'Could not load suggestions')); }
    finally { setBusy(false); }
  };

  const toSave = (rows || []).filter((r) => !r.already_assigned && r.chosen);

  const save = async () => {
    if (!toSave.length) { toast.error('Pick a pay group for at least one employee first'); return; }
    setBusy(true);
    try {
      const { data } = await payrollAPI.payGroupBulk(toSave.map((r) => ({ employee_id: r.employee_id, pay_group: r.chosen, effective_date: eff })));
      toast.success(`Assigned ${data.inserted} employee(s)`);
      load();
      onSaved && onSaved();
    } catch (e) { toast.error(extractErrorMessage(e, 'Nothing saved')); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="payroll-suggest-pay-groups">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5" /> Suggest pay groups from compensation type</CardTitle>
        <CardDescription>Hourly → Officer, Salary → Salaried/admin. Only rows where you pick a pay group are saved — employees with no suggestion are left blank and skipped until you choose.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3">
          <div><label className="text-xs text-slate-500">Effective date</label><Input type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="w-44" data-testid="suggest-eff" /></div>
          <Button variant="outline" onClick={load} disabled={busy} data-testid="suggest-load">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suggest'}</Button>
          <Button onClick={save} disabled={busy || !rows || toSave.length === 0} data-testid="suggest-save">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving…</> : `Save accepted${toSave.length ? ` (${toSave.length})` : ''}`}
          </Button>
        </div>
        {restricted && <Alert className="border-amber-200 bg-amber-50"><Lock className="h-4 w-4 text-amber-600" /><AlertDescription className="text-amber-800 text-sm">{restricted}</AlertDescription></Alert>}
        {rows && (
          <div className="overflow-auto max-h-96 border rounded">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Comp type</TableHead>
                <TableHead>Suggested</TableHead><TableHead>Assign as</TableHead><TableHead>State</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.employee_id} data-testid={`suggest-row-${r.employee_number || r.employee_id}`}>
                    <TableCell className="font-mono text-xs">{r.employee_number} · {r.name}</TableCell>
                    <TableCell className="text-xs">{r.pay_type || '—'}</TableCell>
                    <TableCell className="text-xs capitalize">{r.suggested_pay_group || '—'}</TableCell>
                    <TableCell>
                      <select className="text-xs border rounded px-1 py-0.5" value={r.chosen} disabled={r.already_assigned}
                        onChange={(e) => setRows((prev) => prev.map((x, j) => j === i ? { ...x, chosen: e.target.value } : x))}
                        data-testid={`suggest-select-${r.employee_number || r.employee_id}`}>
                        <option value="">— choose —</option>
                        {PAY_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </TableCell>
                    <TableCell className="text-xs">{r.already_assigned ? <Badge variant="secondary">already assigned</Badge> : <Badge variant="outline">new</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CurrentAssignments({ refreshKey }) {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await payrollAPI.assignments();
      setAll(data.assignments || []);
    } catch (e) { toast.error(extractErrorMessage(e, 'Could not load assignments')); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const ql = q.trim().toLowerCase();
  const rows = (all || []).filter((a) => !ql
    || (a.name || '').toLowerCase().includes(ql)
    || (a.employee_number || '').toLowerCase().includes(ql)
    || (a.pay_group || '').toLowerCase().includes(ql)
    || (a.classification || '').toLowerCase().includes(ql));

  return (
    <Card data-testid="payroll-current-assignments">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5" /> Current assignments</CardTitle>
            <CardDescription>The latest-effective pay group and insurance classification per employee, and who set it.</CardDescription>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee, pay group, classification"
              className="pl-8 w-72" data-testid="assignments-search" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {busy ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center" data-testid="assignments-empty">
            {all && all.length ? 'No assignments match your search.' : 'No pay groups or classifications assigned yet.'}
          </p>
        ) : (
          <div className="overflow-auto max-h-96 border rounded">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Pay group</TableHead>
                <TableHead>Classification</TableHead><TableHead>Effective</TableHead><TableHead>Set by</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.employee_id} data-testid={`assignment-row-${a.employee_number || a.employee_id}`}>
                    <TableCell className="text-xs font-mono">{a.employee_number || '—'}{a.name ? ` · ${a.name}` : ''}</TableCell>
                    <TableCell className="text-xs capitalize">{a.pay_group ? <Badge variant="outline">{a.pay_group}</Badge> : <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-xs">{a.classification || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-xs">{a.pay_group_effective || a.classification_effective || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{a.pay_group_set_by || a.classification_set_by || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PayrollSetup() {
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((x) => x + 1);
  return (
    <div className="space-y-6" data-testid="payroll-setup-tab">
      <Alert className="border-sky-200 bg-sky-50">
        <ShieldCheck className="h-4 w-4 text-sky-600" />
        <AlertDescription className="text-sky-800 text-sm">
          <strong>Payroll setup.</strong> Pay groups, pay periods and insurance classifications for the parallel test.
          Effective-dated and audited. Executives appear only to users with the Exec Comp grant.
        </AlertDescription>
      </Alert>

      <CurrentAssignments refreshKey={refresh} />

      <GeneratePeriods />

      <HoursTypeMapCard refreshKey={refresh} />

      <ExtraPayCard refreshKey={refresh} />

      <CsvUpload
        title="Bulk officer pay rates" kind="officer-rates" templateFile="officer_pay_rates_template.xlsx"
        description="CSV columns: employee_number, job_number, billing_item_name, pay_rate, effective_date."
        cols={['employee_number', 'job_number', 'billing_item_name', 'pay_rate', 'effective_date']}
        reviewFn={payrollAPI.officerRateReview} commitFn={payrollAPI.officerRateCommit} onSaved={bump}
      />

      <SuggestPayGroups onSaved={bump} />

      <CsvUpload
        title="Bulk pay groups" kind="pay-groups" templateFile="pay_groups_template.xlsx"
        description="CSV columns: employee_number, pay_group (officer|salaried), effective_date."
        cols={['employee_number', 'pay_group', 'effective_date']}
        reviewFn={payrollAPI.payGroupReview} commitFn={payrollAPI.payGroupCommit} onSaved={bump}
      />

      <CsvUpload
        title="Bulk insurance classifications" kind="classifications" templateFile="classifications_template.xlsx"
        description="CSV columns: employee_number, classification, effective_date, gl_code, workers_comp_code (GL/WC optional)."
        cols={['employee_number', 'classification', 'effective_date', 'gl_code', 'workers_comp_code']}
        reviewFn={payrollAPI.classReview} commitFn={payrollAPI.classCommit} onSaved={bump}
      />
    </div>
  );
}
